import { useEffect, useState } from 'react';
import { Tabs, Tab, Form, Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Table from '../../../core/common/dataTable/index';
import { RootState } from '../../../core/data/redux/store';
import {
  setStates,
  setDistricts,
  setBlocks,
  setVillages,
  setLoading,
  setError,
  deleteDistrict,
  deleteBlock,
  deleteVillages,
} from '../../../core/data/redux/stateDistrictSlice';
import {
  fetchStates,
  fetchDistricts,
  fetchBlocks,
  fetchVillages,
  deleteDistrict as deleteDistrictService,
  deleteBlock as deleteBlockService,
  deleteVillage as deleteVillageService,
} from '../../../core/data/services/stateDistrictBlockService';
import { State, District, Block, Village } from '../../../core/data/types/stateDistrict';
import StateForm from './stateForm';
import DistrictForm from './districtForm';
import BlockForm from './blockForm';
import VillageForm from './villageForm';

const Admin = () => {
  const dispatch = useDispatch();
  const { states, districts, blocks, villages, loading } = useSelector((state: RootState) => state.stateDistrict);

  // State management
  const [selectedState, setSelectedState] = useState<number | undefined>(undefined);
  const [selectedDistrict, setSelectedDistrict] = useState<number | undefined>(undefined);
  const [selectedBlock, setSelectedBlock] = useState<number | undefined>(undefined);
  const [selectedVillage, setSelectedVillage] = useState<number | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<string>('state');

  // Modal states
  const [showStateForm, setShowStateForm] = useState(false);
  const [showDistrictForm, setShowDistrictForm] = useState(false);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [showVillageForm, setShowVillageForm] = useState(false);
  const [editState, setEditState] = useState<State | undefined>(undefined);
  const [editDistrict, setEditDistrict] = useState<District | undefined>(undefined);
  const [editBlock, setEditBlock] = useState<Block | undefined>(undefined);
  const [editVillage, setEditVillage] = useState<Village | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState<{ id: number; type: 'state' | 'district' | 'block' | 'village' | 'school' } | null>(null);

  // Fetch states on mount
  useEffect(() => {
    const loadStates = async () => {
      dispatch(setLoading(true));
      try {
        const response = await fetchStates();
        dispatch(setStates(response));
      } catch (err: any) {
        dispatch(setError(err.message || 'Failed to fetch states'));
      } finally {
        dispatch(setLoading(false));
      }
    };
    loadStates();
  }, [dispatch]);

  // Fetch districts when a state is selected
  useEffect(() => {
    const loadDistricts = async () => {
      if (selectedState) {
        dispatch(setLoading(true));
        try {
          const response = await fetchDistricts(selectedState);
          // Ensure ids are numeric if API returns strings
          const normalized = Array.isArray(response)
            ? response.map(d => ({ ...d, id: Number(d.id), stateId: Number(d.stateId) }))
            : [];
          dispatch(setDistricts(normalized));
        } catch (err: any) {
          dispatch(setError(err.message || 'Failed to fetch districts'));
          dispatch(setDistricts([]));
        } finally {
          dispatch(setLoading(false));
        }
      } else {
        dispatch(setDistricts([]));
        dispatch(setBlocks([]));
        dispatch(setVillages([]));
        setSelectedDistrict(undefined);
        setSelectedBlock(undefined);
        setSelectedVillage(undefined);
      }
    };
    loadDistricts();
  }, [selectedState, dispatch]);

  // Fetch blocks when a district is selected
  useEffect(() => {
    const loadBlocks = async () => {
      if (selectedDistrict) {
        dispatch(setLoading(true));
        try {
          const response = await fetchBlocks(selectedState, selectedDistrict);
          // Normalize ids to numbers to match select values
          const normalized = Array.isArray(response)
            ? response.map(b => ({ ...b, id: Number(b.id), districtId: Number(b.districtId), stateId: Number(b.stateId) }))
            : [];
          dispatch(setBlocks(normalized));
        } catch (err: any) {
          dispatch(setError(err.message || 'Failed to fetch blocks'));
          dispatch(setBlocks([]));
        } finally {
          dispatch(setLoading(false));
        }
      } else {
        dispatch(setBlocks([]));
        dispatch(setVillages([]));
        setSelectedBlock(undefined);
        setSelectedVillage(undefined);
      }
    };
    loadBlocks();
  }, [selectedState, selectedDistrict, dispatch]);

  // Fetch villages when a block is selected
  useEffect(() => {
    const loadVillages = async () => {
      if (selectedBlock) {
        dispatch(setLoading(true));
        try {
          const response = await fetchVillages(selectedState, selectedDistrict, selectedBlock);
          const normalized = Array.isArray(response)
            ? response.map(v => ({ ...v, id: Number(v.id), blockAreaId: Number(v.blockAreaId) }))
            : [];
          dispatch(setVillages(normalized));
        } catch (err: any) {
          dispatch(setError(err.message || 'Failed to fetch villages'));
          dispatch(setVillages([]));
        } finally {
          dispatch(setLoading(false));
        }
      } else {
        dispatch(setVillages([]));
        setSelectedVillage(undefined);
      }
    };
    loadVillages();
  }, [selectedState, selectedDistrict, selectedBlock, dispatch]);
  

  // Debug log for filtered data
  useEffect(() => {
    console.log('Filtered blocks:', blocks);
    console.log('Active tab:', activeTab);
    console.log('Selected district:', selectedDistrict);
  }, [blocks, activeTab, selectedDistrict]);

  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      dispatch(setLoading(true));
      if (deleteItem.type === 'district') {
        await deleteDistrictService(deleteItem.id);
        dispatch(deleteDistrict(deleteItem.id));
        setSelectedDistrict(undefined);
        setSelectedBlock(undefined);
        setSelectedVillage(undefined);
        if (selectedState) {
          const d = await fetchDistricts(selectedState);
          const normalized = Array.isArray(d)
            ? d.map(x => ({ ...x, id: Number(x.id), stateId: Number(x.stateId) }))
            : [];
          dispatch(setDistricts(normalized));
        }
      } else if (deleteItem.type === 'block') {
        await deleteBlockService(deleteItem.id);
        dispatch(deleteBlock(deleteItem.id));
        setSelectedBlock(undefined);
        setSelectedVillage(undefined);
        if (selectedDistrict) {
          const b = await fetchBlocks(selectedState, selectedDistrict);
          const normalized = Array.isArray(b)
            ? b.map(x => ({ ...x, id: Number(x.id), districtId: Number(x.districtId), stateId: Number(x.stateId) }))
            : [];
          dispatch(setBlocks(normalized));
        }
      } else if (deleteItem.type === 'village') {
        await deleteVillageService(deleteItem.id);
        dispatch(deleteVillages(deleteItem.id));
        setSelectedVillage(undefined);
        if (selectedBlock) {
          const v = await fetchVillages(selectedState, selectedDistrict, selectedBlock);
          const normalized = Array.isArray(v)
            ? v.map(x => ({ ...x, id: Number(x.id), blockAreaId: Number(x.blockAreaId) }))
            : [];
          dispatch(setVillages(normalized));
        }
      }
      setShowDeleteModal(false);
      setDeleteItem(null);
    } catch (err: any) {
      dispatch(setError(err.message || 'Failed to delete'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Table columns with action buttons
  const stateColumns = [
    { title: 'ID', dataIndex: 'id', sorter: (a: State, b: State) => a.id - b.id },
    { title: 'Name', dataIndex: 'name', sorter: (a: State, b: State) => a.name.localeCompare(b.name) },
    {
      title: 'Actions',
      render: (record: State) => (
        <>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              setEditState(record);
              setShowStateForm(true);
            }}
          >
            Edit
          </Button>
        </>
      ),
    },
  ];

  const districtColumns = [
    { title: 'ID', dataIndex: 'id', sorter: (a: District, b: District) => a.id - b.id },
    { title: 'Name', dataIndex: 'name', sorter: (a: District, b: District) => a.name.localeCompare(b.name) },
    { title: 'State ID', dataIndex: 'stateId', sorter: (a: District, b: District) => a.stateId - b.stateId },
    {
      title: 'Actions',
      render: (record: District) => (
        <>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              setEditDistrict(record);
              setShowDistrictForm(true);
            }}
          >
            Edit
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-2"
            onClick={() => {
              setDeleteItem({ id: record.id, type: 'district' });
              setShowDeleteModal(true);
            }}
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  const blockColumns = [
    { title: 'ID', dataIndex: 'id', sorter: (a: Block, b: Block) => a.id - b.id },
    { title: 'Name', dataIndex: 'name', sorter: (a: Block, b: Block) => a.name.localeCompare(b.name) },
    { title: 'District ID', dataIndex: 'districtId', sorter: (a: Block, b: Block) => a.districtId - b.districtId },
    {
      title: 'Actions',
      render: (record: Block) => (
        <>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              setEditBlock(record);
              setShowBlockForm(true);
            }}
          >
            Edit
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-2"
            onClick={() => {
              setDeleteItem({ id: record.id, type: 'block' });
              setShowDeleteModal(true);
            }}
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  const villageColumns = [
    { title: 'ID', dataIndex: 'id', sorter: (a: Village, b: Village) => a.id - b.id },
    { title: 'Name', dataIndex: 'name', sorter: (a: Village, b: Village) => a.name.localeCompare(b.name) },
    { title: 'Block ID', dataIndex: 'blockAreaId', sorter: (a: Village, b: Village) => a.blockAreaId - b.blockAreaId },
    {
      title: 'Actions',
      render: (record: Village) => (
        <>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              setEditVillage(record);
              setShowVillageForm(true);
            }}
          >
            Edit
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-2"
            onClick={() => {
              setDeleteItem({ id: record.id, type: 'village' });
              setShowDeleteModal(true);
            }}
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  // Filter data based on selections and active tab
  const filteredStates = selectedState && activeTab === 'state' ? states.filter((state) => state.id === selectedState) : states;
  const filteredDistricts = Array.isArray(districts)
    ? selectedDistrict && activeTab === 'districts'
      ? districts.filter((district) => district.id === selectedDistrict && district.stateId === selectedState)
      : selectedState
        ? districts.filter((district) => district.stateId === selectedState)
        : districts
    : [];
  const filteredBlocks = Array.isArray(blocks)
    ? selectedBlock && activeTab === 'blocks'
      ? blocks.filter((block) => block.id === selectedBlock && block.districtId === selectedDistrict)
      : selectedDistrict
        ? blocks.filter((block) => block.districtId === selectedDistrict)
        : blocks
    : [];
  const filteredVillages = Array.isArray(villages)
    ? selectedVillage && activeTab === 'villages'
      ? villages.filter((village) => village.id === selectedVillage && village.blockAreaId === selectedBlock)
      : selectedBlock
        ? villages.filter((village) => village.blockAreaId === selectedBlock)
        : villages
    : [];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="page-title">Admin Management</h3>
          {/* <Button
            variant="primary"
            onClick={() => {
              if (activeTab === 'state') {
                setEditState(undefined);
                setShowStateForm(true);
              } else if (activeTab === 'districts' && selectedState) {
                setEditDistrict(undefined);
                setShowDistrictForm(true);
              } else if (activeTab === 'blocks' && selectedState && selectedDistrict) {
                // Clear block filter before opening Add to show all blocks after save
                setSelectedBlock(undefined);
                setEditBlock(undefined);
                setShowBlockForm(true);
              } else if (activeTab === 'villages' && selectedBlock) {
                setEditVillage(undefined);
                setShowVillageForm(true);
              }
            }}
            disabled={
              (activeTab === 'districts' && !selectedState) ||
              (activeTab === 'blocks' && (!selectedState || !selectedDistrict)) ||
              (activeTab === 'villages' && !selectedBlock)
            }
          >
            {activeTab === 'state' ? 'Add State' : activeTab === 'districts' ? 'Add District' : activeTab === 'blocks' ? 'Add Block' : 'Add Village'}
          </Button> */}
        </div>

        {/* Dropdowns and Tabs */}
        <div className="row mb-3 align-items-end">
          <div className="col-md-3">
            <Form.Group>
              <Form.Label>Select State</Form.Label>
              <Form.Select
                value={selectedState || ''}
                onChange={(e) => {
                  const next = e.target.value ? Number(e.target.value) : undefined;
                  setSelectedState(next);
                  setSelectedDistrict(undefined);
                  setSelectedBlock(undefined);
                  setSelectedVillage(undefined);
                  setActiveTab('state');
                }}
              >
                <option value="">Select a state</option>
                {states.map((state: any) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-3">
            <Form.Group>
              <Form.Label>Select District</Form.Label>
              <Form.Select
                value={selectedDistrict || ''}
                onChange={(e) => {
                  const next = e.target.value ? Number(e.target.value) : undefined;
                  setSelectedDistrict(next);
                  setSelectedBlock(undefined);
                  setSelectedVillage(undefined);
                  setActiveTab('districts');
                }}
                disabled={!selectedState}
              >
                <option value="">Select a district</option>
                {Array.isArray(districts) &&
                  districts
                    .filter((district) => district.stateId === selectedState)
                    .map((district) => (
                      <option key={district.id} value={district.id}>
                        {district.name}
                      </option>
                    ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-3">
            <Form.Group>
              <Form.Label>Select Block</Form.Label>
              <Form.Select
                value={selectedBlock || ''}
                onChange={(e) => {
                  const next = e.target.value ? Number(e.target.value) : undefined;
                  setSelectedBlock(next);
                  setSelectedVillage(undefined);
                  setActiveTab('blocks');
                }}
                disabled={!selectedDistrict}
              >
                <option value="">Select a block</option>
                {Array.isArray(blocks) &&
                  blocks
                    .filter((block) => block.districtId === selectedDistrict)
                    .map((block) => (
                      <option key={block.id} value={block.id}>
                        {block.name}
                      </option>
                    ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-3">
            <Form.Group>
              <Form.Label>Select Village</Form.Label>
              <Form.Select
                value={selectedVillage || ''}
                onChange={(e) => {
                  const next = e.target.value ? Number(e.target.value) : undefined;
                  setSelectedVillage(next);
                  setActiveTab('villages');
                }}
                disabled={!selectedBlock}
              >
                <option value="">Select a village</option>
                {Array.isArray(villages) &&
                  villages
                    .filter((village) => village.blockAreaId === selectedBlock)
                    .map((village) => (
                      <option key={village.id} value={village.id}>
                        {village.name}
                      </option>
                    ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-4 d-flex mt-3">
            <Tabs
              activeKey={activeTab}
              onSelect={(key) => setActiveTab(key || 'state')}
              id="location-tabs"
              className="mb-0"
            >
              <Tab eventKey="state" title="States" />
              <Tab eventKey="districts" title="Districts" disabled={!selectedState} />
              <Tab eventKey="blocks" title="Blocks" disabled={!selectedDistrict} />
              <Tab eventKey="villages" title="Villages" disabled={!selectedBlock} />
            </Tabs>
          </div>
          <div className="col-md-6 d-flex  mt-3 align-items-end justify-content-end">
            <Button
              variant="primary"
              onClick={() => {
                if (activeTab === 'state') {
                  setEditState(undefined);
                  setShowStateForm(true);
                } else if (activeTab === 'districts' && selectedState) {
                  setEditDistrict(undefined);
                  setShowDistrictForm(true);
                } else if (activeTab === 'blocks' && selectedDistrict) {
                  setEditBlock(undefined);
                  setShowBlockForm(true);
                } else if (activeTab === 'villages' && selectedBlock) {
                  setEditVillage(undefined);
                  setShowVillageForm(true);
                }
              }}
              disabled={
                (activeTab === 'districts' && !selectedState) ||
                (activeTab === 'blocks' && !selectedDistrict) ||
                (activeTab === 'villages' && !selectedBlock)
              }
            >
              {activeTab === 'state' ? 'Add State' : activeTab === 'districts' ? 'Add District' : activeTab === 'blocks' ? 'Add Block' : 'Add Village'}
            </Button>
          </div>
        </div>


        {/* Table */}
        <div className="card">
          <div className="card-body p-0 py-3">
            {loading ? (
              <div className="text-center py-5">
                <i className="bi bi-arrow-repeat animate-spin" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3">Loading...</h5>
              </div>
            ) : activeTab === 'state' && states.length === 0 ? (
              <div className="text-center py-5">
                <h5 className="mt-3 text-muted">No States Found</h5>
              </div>
            ) : activeTab === 'districts' && filteredDistricts.length === 0 ? (
              <div className="text-center py-5">
                <h5 className="mt-3 text-muted">No Districts Found</h5>
              </div>
            ) : activeTab === 'blocks' && filteredBlocks.length === 0 ? (
              <div className="text-center py-5">
                <h5 className="mt-3 text-muted">No Blocks Found</h5>
              </div>
            ) : activeTab === 'villages' && filteredVillages.length === 0 ? (
              <div className="text-center py-5">
                <h5 className="mt-3 text-muted">No Villages Found</h5>
              </div>
            ) : (
              <Table
                key={`${activeTab}-${selectedDistrict || ''}-${blocks.length}`} // remount on data size change
                dataSource={
                  activeTab === 'state'
                    ? filteredStates
                    : activeTab === 'districts'
                      ? filteredDistricts
                      : activeTab === 'blocks'
                        ? filteredBlocks
                        : filteredVillages
                }
                columns={
                  activeTab === 'state'
                    ? stateColumns
                    : activeTab === 'districts'
                      ? districtColumns
                      : activeTab === 'blocks'
                        ? blockColumns
                        : villageColumns
                }
                Selection={false}
                pagination={true}
              />
            )}
          </div>
        </div>

        {/* Modals */}
        <StateForm show={showStateForm} onHide={() => setShowStateForm(false)} state={editState} />
        {selectedState && (
          <DistrictForm
            show={showDistrictForm}
            onHide={() => setShowDistrictForm(false)}
            district={editDistrict}
            stateId={selectedState}
          />
        )}
        {selectedDistrict && (
          <BlockForm
            show={showBlockForm}
            onHide={() => setShowBlockForm(false)}
            block={editBlock}
            stateId={selectedState!}
            districtId={selectedDistrict}
            onSuccess={() => {
              // Ensure Blocks tab stays active and filter is cleared so new block is visible
              setSelectedBlock(undefined);
              setActiveTab('blocks');
            }}
          />
        )}
        {selectedBlock && (
          <VillageForm
            show={showVillageForm}
            onHide={() => setShowVillageForm(false)}
            village={editVillage}
            stateId={selectedState!}
            districtId={selectedDistrict!}
            blockAreaId={selectedBlock}
          />
        )}

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this {deleteItem?.type}?
          </Modal.Body>
          <Modal.Footer className='gap-2'>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default Admin;


